import type { SupabaseClient } from "@supabase/supabase-js";
import { CAPTURE_NON_ENREGISTREE, httpDetail } from "../captures/upload-error";
import { dbErrorMessage } from "../supabase/db-error";

/** `detail` : motif technique, jamais affiché (classement de l'échec de synchro). */
export type EnsureProfileResult = { ok: true } | { ok: false; error: string; detail?: string };

// « thierry.poitras » → « Thierry Poitras » : points/underscores = espaces,
// majuscule à chaque mot et après trait d'union (« jean-marc » → « Jean-Marc »).
// Heuristique : les accents restent perdus tant qu'il n'y a pas d'édition de profil.
export function displayNameFromEmail(email: string | null | undefined): string {
  if (!email) return "utilisateur";
  const at = email.indexOf("@");
  if (at <= 0) return "utilisateur";
  const words = email.slice(0, at).split(/[._]+/).filter(Boolean);
  if (words.length === 0) return "utilisateur";
  return words
    .map((word) =>
      word
        .split("-")
        .map((seg) => seg.charAt(0).toUpperCase() + seg.slice(1))
        .join("-"),
    )
    .join(" ");
}

interface ProfileInput {
  id: string;
  email: string | null | undefined;
}

export async function ensureProfile(
  client: SupabaseClient,
  input: ProfileInput,
): Promise<EnsureProfileResult> {
  // ignoreDuplicates : un profil existant n'est jamais écrasé, pour qu'une
  // correction manuelle du display_name (accents, graphie) survive aux synchros.
  const { error, status } = await client
    .from("profiles")
    .upsert(
      { id: input.id, display_name: displayNameFromEmail(input.email) },
      { onConflict: "id", ignoreDuplicates: true },
    );
  // DS §6.13 : ce résultat remonte jusqu'à l'écran de capture via
  // `syncCaptureToServer` — l'audio est encore sur l'appareil, le message le dit.
  if (error) {
    return {
      ok: false,
      error: dbErrorMessage("profile.ensure", error, CAPTURE_NON_ENREGISTREE),
      detail: httpDetail(error.message, status),
    };
  }
  return { ok: true };
}
